import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faRefresh } from '@fortawesome/free-solid-svg-icons';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: var(--space-8);
  text-align: center;
  background: var(--background-color);
  color: var(--text-color);
  font-family: var(--font-family);
  font-weight: 400;
`;

const ErrorIcon = styled.div`
  font-size: var(--text-4xl);
  color: var(--error-600);
  margin-bottom: var(--space-4);
`;

const ErrorTitle = styled.h1`
  font-size: var(--text-3xl);
  margin-bottom: var(--space-4);
  color: var(--text-color);
  font-family: var(--font-family);
  font-weight: 400;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  font-size: var(--text-lg);
  margin-bottom: var(--space-8);
  color: var(--text-color);
  opacity: 0.8;
  max-width: 600px;
  line-height: 1.6;
  font-family: var(--font-family);
  font-weight: 400;
`;

const ErrorDetails = styled.details`
  margin-bottom: var(--space-8);
  max-width: 800px;

  summary {
    cursor: pointer;
    font-weight: 600;
    margin-bottom: var(--space-4);
    color: var(--primary-600);
    font-family: var(--font-family);
  font-weight: 400;
    font-size: var(--text-base);
    transition: color 0.3s ease;

    &:hover {
      color: var(--primary-700);
    }
  }

  pre {
    background: var(--neutral-100);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
    text-align: left;
    font-size: var(--text-sm);
    font-family: var(--font-mono);
    overflow-x: auto;
    border: 1px solid var(--border-color-light);
    line-height: 1.5;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-6);
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-800) 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(90, 99, 230, 0.4);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }

  svg {
    font-size: var(--text-base);
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorIcon>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </ErrorIcon>
          <ErrorTitle style={{
            fontFamily: 'var(--font-heading-bold)',
            marginBottom: 'var(--space-4)',
            color: 'var(--text-color)',
            fontSize: 'var(--text-3xl)',
            fontWeight: '600'
          }}>שגיאה באפליקציה</ErrorTitle>
          <ErrorMessage>
            אירעה שגיאה לא צפויה באפליקציה. אנא רענן את הדף כדי לנסות שוב.
            <br />
            אם השגיאה ממשיכה, ייתכן שיש בעיה בקבצי ההגדרה או בחיבור לאינטרנט.
          </ErrorMessage>

          <ErrorDetails>
            <summary style={{
              fontFamily: 'var(--font-heading-medium)',
              fontSize: 'var(--text-base)',
              fontWeight: '400',
              color: 'var(--primary-600)',
              marginBottom: 'var(--space-4)',
              cursor: 'pointer'
            }}>פרטי השגיאה (לחץ כדי לראות)</summary>
            <pre>
              {this.state.error && this.state.error.toString()}
              {this.state.errorInfo && `\n\n${this.state.errorInfo.componentStack}`}
            </pre>
          </ErrorDetails>

          <RefreshButton onClick={this.handleRefresh} style={{
            fontFamily: 'var(--font-heading-regular)',
            fontSize: 'var(--text-base)',
            fontWeight: '400'
          }}>
            <FontAwesomeIcon icon={faRefresh} />
            רענן דף
          </RefreshButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
